import React, { useContext, useEffect, useRef, useState } from "react";
import DataService from "../../../services/DataService.service";
import { AppSettingContext } from "../../Application";
import LoadingComponent from "../../shared/LoadingComponent";

interface IActivateGiftCardFormProps {
    cacheActNum: string;
    proceedToNextStep(): void;
    handleActNumberUpdate(actNumber: string): void;
}

interface IActivationNumberStatus {
    status: "" | "success" | "warning" | "error";
    message: string;
}

const ActivateGiftCardForm = (props: IActivateGiftCardFormProps) => {
    const { labels, activateSettings } = useContext(AppSettingContext);

    const firstRender = useRef(true);
    const [fractionRef1, fractionRef2, fractionRef3] = [useRef(null), useRef(null), useRef(null)];

    //#region STATE PROPERTIES

    const [actNumber, setActNumber] = useState({
        fraction1: props.cacheActNum.slice(0, 4) ?? "",
        fraction2: props.cacheActNum.slice(4, 8) ?? "",
        fraction3: props.cacheActNum.slice(8, 12) ?? "",

        getFull() {
            return this.fraction1 + this.fraction2 + this.fraction3;
        }
    });
    const [actNumberState, setActNumberState] = useState<IActivationNumberStatus>();
    const [isLoading, setIsLoading] = useState(false);
    const [isStepActive, setIsStepActive] = useState(false);

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        if (firstRender.current && !actNumber.getFull()) {
            firstRender.current = false;
            return;
        }

        setIsLoading(true);
        setIsStepActive(true);

        const actNumStr = actNumber.getFull();
        props.handleActNumberUpdate(actNumStr);
        validateActNumber(actNumStr);

        setIsLoading(false);

        // if there already is activation number stored in the cache
        // automatically submit and proceed to next step
        if (actNumber.getFull().length > 0 &&
            actNumberState?.status !== "error" &&
            props.cacheActNum.length > 0 &&
            actNumber.getFull() === props.cacheActNum
        ) {
            proceedToNextStep();
        }
    }, [actNumber]);

    //#endregion

    //#region HANDLERS

    const handleActNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d*$/;
        if (!pattern.test(e.target.value)) {
            e.preventDefault();
            return;
        }

        if (e.target.value.length === 4) {
            switch (e.target.name) {
                case "fraction1":
                    fractionRef2.current?.focus();
                    break;
                case "fraction2":
                    fractionRef3.current?.focus();
                    break;
            }
        }

        setActNumber({ ...actNumber, [e.target.name]: e.target.value });
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();

        const pasteData = event.clipboardData.getData('text');

        const numericPattern = /^\d*$/;
        if (!numericPattern.test(pasteData)) {
            event.preventDefault();
            return;
        }

        const fraction1 = pasteData.slice(0, 4);
        const fraction2 = pasteData.slice(4, 8);
        const fraction3 = pasteData.slice(8, 12);

        setActNumber({ ...actNumber, fraction1, fraction2, fraction3 });

        if (pasteData.length >= 4 && pasteData.length < 8) {
            fractionRef2.current?.focus();
        } else if (pasteData.length >= 8) {
            fractionRef3.current?.focus();
        }
    };

    //#endregion

    //#region HELPERS

    const validateActNumber = async (actNumber: string) => {
        if (actNumber.length !== activateSettings.codeLength) {
            setActNumberState({
                status: "",
                message: ""
            });

            return;
        }

        // first check if the activation number could be valid luhn by its pattern
        const isValidLuhn = await DataService.checkLuhnPattern(actNumber);

        if (isValidLuhn) {
            // pattern is valid
            setActNumberState({
                status: "",
                message: ""
            });
        } else {
            // pattern is invalid
            setActNumberState({
                status: "error",
                message: labels.get("GiftCard.App.ActivateLoadCard.Errors.ActivationNo.Error")
            });
        }
    };

    const proceedToNextStep = async () => {
        if (actNumberState?.status === "error") {
            return;
        }

        setIsLoading(true);

        const actNumStr = actNumber.getFull();
        const response = await DataService.checkGiftCardCode(actNumStr);

        if (response.status.toLowerCase() === "ok") {
            setActNumberState({
                status: "success",
                message: labels.get("GiftCard.App.ActivateLoadCard.Errors.ActivationNo.Valid")
            });
        } else if (response.status.toLowerCase() === "warning") {
            setActNumberState({
                status: "warning",
                message: labels.get("GiftCard.App.ActivateLoadCard.Errors.ActivationNo.Warning")
            });

            setIsLoading(false);
            return;
        } else if (response.status.toLowerCase() === "error") {
            console.error(response.message);

            setActNumberState({
                status: "error",
                message: labels.get("GiftCard.App.ActivateLoadCard.Errors.ActivationNo.Error")
            });

            setIsLoading(false);
            return;
        }

        props.proceedToNextStep();
        setIsStepActive(false);
        setIsLoading(false);
    };

    //#endregion

    return (
        <div className="box activate-giftcard">
            {isLoading ? (
                <LoadingComponent />
            ) : (
                <>
                    <label className="">
                        {labels.get("GiftCard.App.ActivateLoadCard.Labels.ActivationNo")}
                    </label>
                    <div className="activate-card-form">
                        <div>
                            <div className="activationNumber-fields">
                                <input
                                    type="text"
                                    className={`activate-input-fraction react-input-${actNumberState?.status ?? ""}`}
                                    value={actNumber.fraction1}
                                    onChange={(e) => handleActNumberChange(e)}
                                    maxLength={4}
                                    name="fraction1"
                                    id="actNumber-fraction1"
                                    placeholder={labels.get("GiftCard.App.ActivateLoadCard.Placeholders.ActivationNo1")}
                                    ref={fractionRef1}
                                    onPaste={handlePaste}
                                />
                                <span className="input-middle-dash">-</span>
                                <input
                                    type="text"
                                    className={`activate-input-fraction react-input-${actNumberState?.status ?? ""}`}
                                    value={actNumber.fraction2}
                                    onChange={(e) => handleActNumberChange(e)}
                                    maxLength={4}
                                    name="fraction2"
                                    id="actNumber-fraction2"
                                    placeholder={labels.get("GiftCard.App.ActivateLoadCard.Placeholders.ActivationNo2")}
                                    ref={fractionRef2}
                                />
                                <span className="input-middle-dash">-</span>
                                <input
                                    type="text"
                                    className={`activate-input-fraction react-input-${actNumberState?.status ?? ""}`}
                                    value={actNumber.fraction3}
                                    onChange={(e) => handleActNumberChange(e)}
                                    maxLength={4}
                                    name="fraction3"
                                    id="actNumber-fraction3"
                                    placeholder={labels.get("GiftCard.App.ActivateLoadCard.Placeholders.ActivationNo3")}
                                    ref={fractionRef3}
                                />
                            </div>
                            {actNumberState?.status && (
                                <div className={`react-input-${actNumberState.status}-message err-msg-container`}>
                                    {actNumberState?.status === 'error' && (
                                        <svg fill="#ff0000" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" width="18px" height="18px" viewBox="0 0 123.996 123.996" xmlSpace="preserve" stroke="#ff0000">
                                            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" stroke="#CCCCCC" strokeWidth="1.23996"></g>
                                            <g id="SVGRepo_iconCarrier">
                                                <g>
                                                    <path d="M9.821,118.048h104.4c7.3,0,12-7.7,8.7-14.2l-52.2-92.5c-3.601-7.199-13.9-7.199-17.5,0l-52.2,92.5 C-2.179,110.348,2.521,118.048,9.821,118.048z M70.222,96.548c0,4.8-3.5,8.5-8.5,8.5s-8.5-3.7-8.5-8.5v-0.2c0-4.8,3.5-8.5,8.5-8.5 s8.5,3.7,8.5,8.5V96.548z M57.121,34.048h9.801c2.699,0,4.3,2.3,4,5.2l-4.301,37.6c-0.3,2.7-2.1,4.4-4.6,4.4s-4.3-1.7-4.6-4.4 l-4.301-37.6C52.821,36.348,54.422,34.048,57.121,34.048z"></path>
                                                </g>
                                            </g>
                                        </svg>
                                    )}

                                    {actNumberState?.status === 'warning' && (
                                        <svg width="18px" height="18px" viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" aria-hidden="true" role="img" className="iconify iconify--noto" preserveAspectRatio="xMidYMid meet" fill="#000000">
                                            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                            <g id="SVGRepo_iconCarrier">
                                                <path d="M57.16 8.42l-52 104c-1.94 4.02-.26 8.85 3.75 10.79c1.08.52 2.25.8 3.45.81h104c4.46-.04 8.05-3.69 8.01-8.15a8.123 8.123 0 0 0-.81-3.45l-52-104a8.067 8.067 0 0 0-14.4 0z" fill="#f2a600"></path>
                                                <path d="M53.56 15.72l-48.8 97.4c-1.83 3.77-.25 8.31 3.52 10.14c.99.48 2.08.74 3.18.76h97.5a7.55 7.55 0 0 0 7.48-7.62a7.605 7.605 0 0 0-.78-3.28l-48.7-97.4a7.443 7.443 0 0 0-9.93-3.47a7.484 7.484 0 0 0-3.47 3.47z" fill="#ffcc32"></path>
                                                <g opacity=".2" fill="#424242">
                                                    <path d="M64.36 34.02c4.6 0 8.3 3.7 8 8l-3.4 48c-.38 2.54-2.74 4.3-5.28 3.92a4.646 4.646 0 0 1-3.92-3.92l-3.4-48c-.3-4.3 3.4-8 8-8"></path>
                                                    <path d="M64.36 98.02c3.31 0 6 2.69 6 6s-2.69 6-6 6s-6-2.69-6-6s2.69-6 6-6"></path>
                                                </g>
                                                <linearGradient id="IconifyId17ecdb2904d178eab21432" gradientUnits="userSpaceOnUse" x1="68" y1="-1808.36" x2="68" y2="-1887.05" gradientTransform="matrix(1 0 0 -1 -3.64 -1776.09)">
                                                    <stop offset="0" stopColor="#424242"></stop>
                                                    <stop offset="1" stopColor="#212121"></stop>
                                                </linearGradient>
                                                <path d="M64.36 34.02c4.6 0 8.3 3.7 8 8l-3.4 48c-.38 2.54-2.74 4.3-5.28 3.92a4.646 4.646 0 0 1-3.92-3.92l-3.4-48c-.3-4.3 3.4-8 8-8z" fill="url(#IconifyId17ecdb2904d178eab21432)"></path>
                                                <linearGradient id="IconifyId17ecdb2904d178eab21433" gradientUnits="userSpaceOnUse" x1="64.36" y1="-1808.36" x2="64.36" y2="-1887.05" gradientTransform="matrix(1 0 0 -1 0 -1772.11)">
                                                    <stop offset="0" stopColor="#424242"></stop>
                                                    <stop offset="1" stopColor="#212121"></stop>
                                                </linearGradient>
                                                <circle cx="64.36" cy="104.02" r="6" fill="url(#IconifyId17ecdb2904d178eab21433)"></circle>
                                                <path d="M53.56 23.02c-1.2 1.5-21.4 41-21.4 41s-1.8 3 .7 4.7c2.3 1.6 4.4-.3 5.3-1.8s19.2-36.9 19.9-38.6c.6-1.87.18-3.91-1.1-5.4c-1.3-1.2-2.6-1-3.4.1z" fill="#fff170"></path>
                                                <circle cx="31.36" cy="75.33" r="3.3" fill="#fff170"></circle>
                                            </g>
                                        </svg>
                                    )}

                                    {actNumberState?.status === 'success' && (
                                        <svg width="20px" height="20px" viewBox="0 0 60.00 60.00" xmlns="http://www.w3.org/2000/svg" fill="#000000" stroke="#000000" strokeWidth="0.0006000000000000001">
                                            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                            <g id="SVGRepo_iconCarrier">
                                                <defs><style>{`.cls-1 {fill: #36BB9A; fill-rule: evenodd;}`}</style></defs>
                                                <path className="cls-1" d="M800,510a30,30,0,1,1,30-30A30,30,0,0,1,800,510Zm-16.986-23.235a3.484,3.484,0,0,1,0-4.9l1.766-1.756a3.185,3.185,0,0,1,4.574.051l3.12,3.237a1.592,1.592,0,0,0,2.311,0l15.9-16.39a3.187,3.187,0,0,1,4.6-.027L817,468.714a3.482,3.482,0,0,1,0,4.846l-21.109,21.451a3.185,3.185,0,0,1-4.552.03Z" id="check" transform="translate(-770 -450)"></path>
                                            </g>
                                        </svg>
                                    )}

                                    {actNumberState.message}
                                </div>
                            )}
                        </div>
                        <button
                            id="btn-activate-next"
                            className="btn btn__full_width btn__primary large-fs btn__large"
                            onClick={proceedToNextStep}
                            disabled={actNumberState?.status === "error" || actNumberState?.status === "warning" || !isStepActive}
                        >
                            {labels.get("GiftCard.App.ActivateLoadCard.Btns.Next")}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default ActivateGiftCardForm;